import { defineStore } from "pinia";
import type { Filters } from "@/types/filter";

// NOTE: Filters must be cleared on full browser refresh.
// We intentionally do NOT persist this store to `localStorage`.
const STORAGE_KEY = "efashion_products_filters_v1";

export interface ProductsStoreState {
  filters: Filters | null;
  savedAt?: number;
}

const defaultState = (): ProductsStoreState => ({
  filters: null,
  savedAt: Date.now(),
});

function loadFromStorage(): ProductsStoreState {
  // Clear any legacy persisted filters (auto-clear on refresh)
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
  return defaultState();
}

function saveToStorage(_state: ProductsStoreState) {
  // Do not persist filters between page loads.
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

export const useProductsStore = defineStore("products", {
  state: () => loadFromStorage(),

  getters: {
    hasFilters: (state): boolean => {
      if (!state.filters) return false;
      return Object.values(state.filters).some(
        (value) => value !== null && value !== undefined && value !== "",
      );
    },
  },

  actions: {
    setFilters(filters: Filters | null) {
      this.filters = filters ? { ...filters } : null;
      saveToStorage(this.$state);
    },

    clearFilters() {
      this.filters = null;
      saveToStorage(this.$state);
    },

    persistNow() {
      saveToStorage(this.$state);
    },
  },
});
